import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { subscribeNewsletter } from "@/lib/api";
import useReveal from "@/hooks/useReveal";
import { ARTICLES } from "@/data/articles";

export default function Blog() {
  useReveal();
  return (
    <div data-testid="page-blog">
      {/* SEZIONE 1 — HERO BLOG */}
      <section className="bv-network text-white" data-testid="blog-hero">
        <div className="bv-container py-20 md:py-28">
          <p className="overline reveal" style={{ color: "#36BFC4" }} data-testid="blog-hero-overline">
            / Aggiornamenti
          </p>
          <h1
            data-testid="blog-hero-title"
            className="h-display text-4xl sm:text-5xl md:text-7xl mt-6 leading-[0.95] max-w-[22ch] reveal"
          >
            News e aggiornamenti<br />
            su bandi <span style={{ color: "#36BFC4" }}>e finanza.</span>
          </h1>
          <p
            data-testid="blog-hero-sub"
            className="mt-8 max-w-2xl text-[16px] md:text-[18px] text-white/75 leading-relaxed reveal"
          >
            Tutti gli aggiornamenti su bandi attivi, finanza agevolata e opportunità per le PMI italiane.
            Pubblicazioni costanti, informazioni verificate.
          </p>
        </div>
      </section>

      {/* SEZIONE 2 — GRIGLIA ARTICOLI */}
      <section className="bv-container py-20 md:py-28" data-testid="blog-grid-section">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16">
          {ARTICLES.map((a, i) => (
            <ArticleCard key={a.id} article={a} index={i} />
          ))}
        </div>
      </section>

      {/* SEZIONE 3 — CTA NEWSLETTER */}
      <NewsletterSection />
    </div>
  );
}

function ArticleCard({ article, index }) {
  return (
    <article
      data-testid={`blog-article-${article.id}`}
      className="group reveal flex flex-col"
      style={{ "--reveal-delay": `${index * 90}ms` }}
    >
      {/* Cover */}
      <Link
        to={`/blog/${article.slug}`}
        className="block aspect-[4/3] overflow-hidden border border-[#0E2E4D]/10 bg-[#ECE7DC] relative"
        data-testid={`blog-article-cover-${article.id}`}
        aria-label={article.title}
      >
        <img
          src={article.cover}
          alt=""
          loading="lazy"
          className="w-full h-full object-cover grayscale-[20%] transition-transform duration-700 group-hover:scale-105"
        />
        <span
          className="absolute top-4 left-4 inline-flex items-center gap-2 bg-white/95 px-3 py-1.5 font-mono text-[10px] tracking-[0.22em] uppercase text-[#2BA8AD]"
          data-testid={`blog-article-category-${article.id}`}
        >
          <article.icon size={12} strokeWidth={2} />
          {article.category}
        </span>
      </Link>

      {/* Meta */}
      <p
        className="mt-6 font-mono text-[11px] tracking-[0.22em] uppercase text-[#0E2E4D]/55"
        data-testid={`blog-article-date-${article.id}`}
      >
        {article.date}
      </p>

      {/* Title */}
      <h2
        className="h-display text-xl md:text-2xl mt-3 leading-[1.15] text-[#0E2E4D]"
        data-testid={`blog-article-title-${article.id}`}
      >
        {article.title}
      </h2>

      {/* Excerpt */}
      <p className="mt-4 text-[15px] text-[#0E2E4D]/70 leading-relaxed line-clamp-2">
        {article.excerpt}
      </p>

      {/* CTA */}
      <Link
        to={`/blog/${article.slug}`}
        data-testid={`blog-article-cta-${article.id}`}
        className="mt-6 inline-flex items-center gap-2 text-[12px] tracking-[0.20em] uppercase font-mono text-[#2BA8AD] link-underline self-start"
      >
        Leggi l'articolo <ArrowRight size={14} />
      </Link>
    </article>
  );
}

function NewsletterSection() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Inserisci un'email valida.");
      return;
    }
    setLoading(true);
    try {
      await subscribeNewsletter(email.trim());
      setDone(true);
      setEmail("");
      toast.success("Iscrizione confermata. Ti scriveremo solo quando vale davvero.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Errore. Riprova tra qualche minuto.";
      toast.error(typeof detail === "string" ? detail : "Errore nell'iscrizione.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      data-testid="blog-newsletter"
      className="text-white"
      style={{ backgroundColor: "#1F8388" }}
    >
      <div className="bv-container py-20 md:py-28">
        <div className="grid grid-cols-12 gap-10 items-center">
          <div className="col-span-12 lg:col-span-7 reveal">
            <h2
              data-testid="blog-newsletter-title"
              className="h-display text-3xl md:text-5xl leading-[1.05] max-w-[22ch]"
            >
              Ricevi gli aggiornamenti<br />
              sui bandi direttamente <span className="opacity-80">via email</span>.
            </h2>
            <p className="mt-5 text-[15px] md:text-[16px] text-white/85 max-w-xl" data-testid="blog-newsletter-sub">
              Nessuno spam. Solo opportunità concrete per la tua azienda.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-5 reveal" style={{ "--reveal-delay": "120ms" }}>
            {done ? (
              <div
                data-testid="blog-newsletter-success"
                className="bg-white text-[#0E2E4D] p-8 border border-white/10"
              >
                <p className="overline" style={{ color: "#1F8388" }}>Iscrizione confermata</p>
                <p className="h-display text-2xl mt-3">Grazie. Ti scriveremo solo quando vale davvero la pena.</p>
                <button
                  data-testid="blog-newsletter-reset"
                  onClick={() => setDone(false)}
                  className="mt-5 inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-mono link-underline"
                >
                  Iscrivi un'altra email <ArrowUpRight size={14} />
                </button>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="blog-newsletter-form"
                className="flex flex-col sm:flex-row gap-3"
                noValidate
              >
                <input
                  data-testid="blog-newsletter-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="indirizzo@email.it"
                  className="flex-1 bg-white text-[#0E2E4D] placeholder:text-[#0E2E4D]/40 px-5 py-4 text-[15px] outline-none border border-white/20 focus:border-white"
                  autoComplete="email"
                  required
                />
                <button
                  data-testid="blog-newsletter-submit"
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center justify-center gap-2 bg-[#0E2E4D] text-white px-7 py-4 text-[12px] tracking-[0.22em] uppercase font-display font-bold hover:bg-[#061A2E] transition-colors disabled:opacity-60"
                >
                  {loading ? <><Loader2 size={14} className="animate-spin" /> Invio…</> : <>Iscriviti</>}
                </button>
              </form>
            )}
            <p className="mt-3 font-mono text-[10px] tracking-[0.22em] uppercase text-white/70">
              GDPR · Iscriviti / cancella in un click
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
